import React, { useEffect, useState } from 'react';
import loadingSprite from '../../assets/loading-sprite.webp';

interface LoadingLogoProps {
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
    className?: string;
}

/**
 * Preloads the webp asset once at module level.
 * If for any reason fetching .webp fails (Chrome ERR_NO_BUFFER_SPACE glitch, Vite dev server quirks,
 * proxy/cache quirks, etc.) we fall back entirely to a pure CSS animated SVG spinner
 * so the user never sees a "broken loading logo" and DevTools console stays clean.
 */
const assetPreloaded: { src: string | null; failed: boolean } = {
    src: (typeof loadingSprite === 'string' ? loadingSprite : null),
    failed: false,
};
function testSpriteOnce(): Promise<boolean> {
    return new Promise((resolve) => {
        if (!assetPreloaded.src) { assetPreloaded.failed = true; return resolve(false); }
        const img = new Image();
        let done = false;
        const finish = (ok: boolean) => { if (done) return; done = true; assetPreloaded.failed = !ok; resolve(ok); };
        img.onload = () => finish(true);
        img.onerror = () => finish(false);
        img.src = assetPreloaded.src;
        // timeout: prevent hanging in weird browser buffer states
        setTimeout(() => finish(true), 2000);
    });
}

export const LoadingLogo: React.FC<LoadingLogoProps & { overrideSize?: number }> = ({
    size = 'md',
    className = '',
    overrideSize
}) => {
    const sizeClasses = {
        sm: 'w-8 h-8',
        md: 'w-12 h-12',
        lg: 'w-16 h-16',
        xl: 'w-24 h-24',
        '2xl': 'w-32 h-32',
        '3xl': 'w-48 h-48',
        '4xl': 'w-64 h-64',
        '5xl': 'w-80 h-80',
    } as const;

    const sizeStyles: Record<string, React.CSSProperties> = {
        sm: { width: '32px', height: '32px', minWidth: '32px', minHeight: '32px' },
        md: { width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' },
        lg: { width: '64px', height: '64px', minWidth: '64px', minHeight: '64px' },
        xl: { width: '96px', height: '96px', minWidth: '96px', minHeight: '96px' },
        '2xl': { width: '128px', height: '128px', minWidth: '128px', minHeight: '128px' },
        '3xl': { width: '192px', height: '192px', minWidth: '192px', minHeight: '192px' },
        '4xl': { width: '256px', height: '256px', minWidth: '256px', minHeight: '256px' },
        '5xl': { width: '320px', height: '320px', minWidth: '320px', minHeight: '320px' },
    };

    const [assetReady, setAssetReady] = useState<boolean>(!assetPreloaded.failed);

    useEffect(() => {
        if (assetPreloaded.failed) { setAssetReady(false); return; }
        testSpriteOnce().then((ok) => setAssetReady(ok));
    }, []);

    const finalStyle: React.CSSProperties = overrideSize ? {
        width: `${overrideSize}px`,
        height: `${overrideSize}px`,
        minWidth: `${overrideSize}px`,
        minHeight: `${overrideSize}px`,
    } : sizeStyles[size];

    const sizeClass = !overrideSize ? sizeClasses[size] : '';

    if (!assetReady || !assetPreloaded.src) {
        // ✅ Fallback: pure CSS/SVG spinner — NO depende de ningún asset externo, NUNCA falla.
        const px = overrideSize
            ? Math.max(16, overrideSize)
            : ({ sm: 32, md: 48, lg: 64, xl: 96, '2xl': 128, '3xl': 192, '4xl': 256, '5xl': 320 } as const)[size];
        const stroke = Math.max(4, Math.round(px / 12));
        return (
            <div
                className={`${sizeClass} ${className} inline-flex items-center justify-center`}
                data-testid="loading-logo"
                style={finalStyle}
            >
                <svg width={px} height={px} viewBox="0 0 50 50" className="animate-spin" aria-label="Loading" role="img">
                    <circle
                        cx="25"
                        cy="25"
                        r="20"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth={stroke * 50 / px}
                    />
                    <circle
                        cx="25"
                        cy="25"
                        r="20"
                        fill="none"
                        stroke="#06b6d4"
                        strokeWidth={stroke * 50 / px}
                        strokeLinecap="round"
                        strokeDasharray="80 160"
                        strokeDashoffset="0"
                    />
                </svg>
            </div>
        );
    }

    return (
        <>
            <div
                className={`${sizeClass} ${className}`}
                data-testid="loading-logo"
                style={{
                    ...finalStyle,
                    backgroundImage: `url('${assetPreloaded.src}')`,
                    backgroundSize: '500% 100%',
                    backgroundPosition: '0 0',
                    animation: 'sprite-play 1s steps(5) infinite',
                }}
            />
            <style>{`
        @keyframes sprite-play {
          from { background-position: 0 0; }
          to { background-position: -500% 0; }
        }
      `}</style>
        </>
    );
};
